import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Manages meta tags, Open Graph, Twitter Cards, and JSON-LD
 * 
 * Usage:
 *   <SEO 
 *     title="Page Title"
 *     description="Page description"
 *     image="/images/og-image.jpg"
 *     type="website"
 *   />
 */

const SITE_NAME = 'All American Dog Club';
const SITE_URL = 'https://allamericandog.club';
const DEFAULT_IMAGE = 'https://allamericandog.club/og-image.jpg';
const DEFAULT_DESCRIPTION = 'A member-driven community for dog lovers. Connect with fellow dog owners, access health resources, find local parks, and join a community that puts dogs first.';
const TWITTER_HANDLE = '@allamericandog';

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  article = null,
  event = null,
  organization = null,
  noindex = false,
}) {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  // Build JSON-LD structured data
  const jsonLd = buildJsonLd({ type, title, description, imageUrl, canonicalUrl, article, event, organization });

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph (Facebook, LinkedIn, etc.) */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Article-specific meta (for blog posts) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
}

/**
 * Build JSON-LD structured data based on page type
 */
function buildJsonLd({ type, title, description, imageUrl, canonicalUrl, article, event, organization }) {
  // Base organization data (always included)
  const orgData = {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'All American Dog Club',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
    },
    description: 'A member-driven community for dog lovers, run by members for the benefit of all dogs and their people.',
    foundingDate: '2025',
    sameAs: [
      'https://twitter.com/allamericandog',
      'https://facebook.com/allamericandogclub',
      'https://instagram.com/allamericandogclub',
    ],
  };

  // Website schema
  const websiteData = {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    publisher: { '@id': `${SITE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // Base page data
  const pageData = {
    '@type': 'WebPage',
    '@id': canonicalUrl,
    url: canonicalUrl,
    name: title || SITE_NAME,
    description: description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    image: imageUrl,
  };

  // Build the graph based on page type
  const graph = [orgData, websiteData, pageData];

  // Add article schema for blog posts
  if (type === 'article' && article) {
    graph.push({
      '@type': 'Article',
      '@id': `${canonicalUrl}#article`,
      headline: title,
      description: description,
      image: imageUrl,
      datePublished: article.publishedTime,
      dateModified: article.modifiedTime || article.publishedTime,
      author: {
        '@type': 'Person',
        name: article.author || 'AADC Staff',
      },
      publisher: { '@id': `${SITE_URL}/#organization` },
      mainEntityOfPage: { '@id': canonicalUrl },
      keywords: article.tags?.join(', '),
    });
  }

  // Add event schema
  if (type === 'event' && event) {
    graph.push({
      '@type': 'Event',
      '@id': `${canonicalUrl}#event`,
      name: event.name,
      description: event.description || description,
      startDate: event.startDate,
      endDate: event.endDate,
      location: event.location ? {
        '@type': event.isOnline ? 'VirtualLocation' : 'Place',
        name: event.location,
        ...(event.isOnline && { url: event.locationUrl }),
      } : undefined,
      image: imageUrl,
      organizer: { '@id': `${SITE_URL}/#organization` },
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: event.isOnline 
        ? 'https://schema.org/OnlineEventAttendanceMode'
        : 'https://schema.org/OfflineEventAttendanceMode',
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/**
 * Pre-built SEO configs for common pages
 */
export const SEO_CONFIGS = {
  home: {
    title: null, // Will just show "All American Dog Club"
    description: 'A member-driven community for dog lovers. Connect with fellow dog owners, access health resources, find local dog parks, and join a community that puts dogs first.',
    url: '/',
  },
  about: {
    title: 'About Us',
    description: 'Learn about All American Dog Club - a member-governed community dedicated to connecting dog people and supporting animal welfare organizations.',
    url: '/about',
  },
  membership: {
    title: 'Membership',
    description: 'Join All American Dog Club. Choose from Free, Full Member, or Premium tiers. Get access to health resources, community forums, local events, and more.',
    url: '/membership',
  },
  blog: {
    title: 'Blog',
    description: 'Dog care tips, health advice, training guides, and community news from All American Dog Club.',
    url: '/blog',
  },
  events: {
    title: 'Events',
    description: 'Find dog-friendly events near you. Meetups, training sessions, adoption events, and more from the AADC community.',
    url: '/events',
  },
  breeds: {
    title: 'Dog Breeds',
    description: 'Explore dog breeds with detailed information on temperament, health, care requirements, and more.',
    url: '/breeds',
  },
  parks: {
    title: 'Dog Parks',
    description: 'Find dog parks near you. Reviews, amenities, busy hours, and check-ins from the AADC community.',
    url: '/parks',
  },
  adopt: {
    title: 'Adopt a Dog',
    description: 'Find adoptable dogs from shelters and rescues partnered with All American Dog Club.',
    url: '/adopt',
  },
  clubs: {
    title: 'Club Directory',
    description: 'Explore dog clubs in our network. Join regional clubs, breed-specific groups, and special interest communities.',
    url: '/clubs',
  },
  marketplace: {
    title: 'Marketplace',
    description: 'Buy and sell dog-related items in the AADC marketplace. Supplies, accessories, and more from trusted community members.',
    url: '/marketplace',
  },
  forums: {
    title: 'Community Forums',
    description: 'Join discussions with fellow dog owners. Ask questions, share experiences, and connect with the AADC community.',
    url: '/forums',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with All American Dog Club. Questions, feedback, partnership inquiries, and support.',
    url: '/contact',
  },
  faq: {
    title: 'FAQ',
    description: 'Frequently asked questions about All American Dog Club membership, features, and community guidelines.',
    url: '/faq',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'All American Dog Club privacy policy. Learn how we protect your data and respect your privacy.',
    url: '/privacy-policy',
  },
};
